import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { clearSession, isExpiring, readSession, writeSession, type StoredSession } from './tokens';

const env = import.meta.env;

export const SERVICE_URLS = {
  auth: env.VITE_HR_AUTH_SERVICE_URL ?? 'http://localhost:3009/api/v1',
  job: env.VITE_HR_JOB_SERVICE_URL ?? 'http://localhost:3010/api/v1',
  candidate: env.VITE_HR_CANDIDATE_SERVICE_URL ?? 'http://localhost:3011/api/v1',
  interviewProcess: env.VITE_HR_INTERVIEW_PROCESS_SERVICE_URL ?? 'http://localhost:3012/api/v1',
  calendar: env.VITE_HR_CALENDAR_SERVICE_URL ?? 'http://localhost:3013/api/v1',
  aiInterview: env.VITE_AI_INTERVIEW_SERVICE_URL ?? 'http://localhost:8000/api/v1',
  billing: env.VITE_HR_BILLING_SERVICE_URL ?? 'http://localhost:3015/api/v1',
} as const;

/** One instance per backend service, matching the platform's service-per-client pattern. */
export const authApi = axios.create({ baseURL: SERVICE_URLS.auth, timeout: 20_000 });
export const jobApi = axios.create({ baseURL: SERVICE_URLS.job, timeout: 30_000 });
export const candidateApi = axios.create({ baseURL: SERVICE_URLS.candidate, timeout: 60_000 });
export const interviewProcessApi = axios.create({ baseURL: SERVICE_URLS.interviewProcess, timeout: 30_000 });
export const calendarApi = axios.create({ baseURL: SERVICE_URLS.calendar, timeout: 30_000 });
export const aiInterviewApi = axios.create({ baseURL: SERVICE_URLS.aiInterview, timeout: 60_000 });
export const billingApi = axios.create({ baseURL: SERVICE_URLS.billing, timeout: 20_000 });

const instances: AxiosInstance[] = [
  authApi,
  jobApi,
  candidateApi,
  interviewProcessApi,
  calendarApi,
  aiInterviewApi,
  billingApi,
];

let refreshInFlight: Promise<StoredSession | null> | null = null;
let onUnauthenticated: (() => void) | null = null;

/** The app registers a callback so a hard 401 can route to the sign-in page once. */
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

/**
 * Refreshes the access token. Concurrent callers share one in-flight request, so a
 * dashboard firing six requests at once cannot burn six single-use refresh tokens.
 */
async function refreshSession(): Promise<StoredSession | null> {
  if (refreshInFlight) return refreshInFlight;

  const current = readSession();
  if (!current) return null;

  refreshInFlight = (async () => {
    try {
      const { data } = await axios.post(
        `${SERVICE_URLS.auth}/auth/refresh`,
        { refreshToken: current.refreshToken },
        { timeout: 15_000 }
      );
      return writeSession(data);
    } catch {
      clearSession();
      onUnauthenticated?.();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function attachAuth(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  // Public endpoints (apply, book, invitation links) opt out.
  if (config.headers.get?.('X-Skip-Auth')) {
    config.headers.delete('X-Skip-Auth');
    return config;
  }

  let session = readSession();
  if (isExpiring(session)) session = await refreshSession();
  if (session) config.headers.set('Authorization', `Bearer ${session.accessToken}`);

  return config;
}

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
  correlationId?: string;
  status?: number;
}

/** Normalises every failure into one shape the UI can render without branching. */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status?: number;
  public readonly details?: unknown;
  public readonly correlationId?: string;

  constructor(shape: ApiErrorShape) {
    super(shape.message);
    this.name = 'ApiError';
    this.code = shape.code;
    this.status = shape.status;
    this.details = shape.details;
    this.correlationId = shape.correlationId;
  }
}

function toApiError(error: AxiosError<{ error?: ApiErrorShape }>): ApiError {
  const status = error.response?.status;
  const payload = error.response?.data?.error;

  if (payload) {
    return new ApiError({ ...payload, status });
  }
  if (error.code === 'ECONNABORTED') {
    return new ApiError({ code: 'TIMEOUT', message: 'That took too long. Try again.', status });
  }
  if (!error.response) {
    return new ApiError({
      code: 'NETWORK_ERROR',
      message: 'Could not reach the server. Check your connection and try again.',
    });
  }
  return new ApiError({ code: 'UNKNOWN', message: error.message || 'Something went wrong', status });
}

for (const instance of instances) {
  instance.interceptors.request.use(attachAuth);

  instance.interceptors.response.use(undefined, async (error: AxiosError<{ error?: ApiErrorShape }>) => {
    const config = error.config as (InternalAxiosRequestConfig & { __retried?: boolean }) | undefined;

    // A single retry after a refresh covers the token expiring mid-flight.
    if (error.response?.status === 401 && config && !config.__retried) {
      config.__retried = true;
      const session = await refreshSession();
      if (session) {
        config.headers.set('Authorization', `Bearer ${session.accessToken}`);
        return instance.request(config);
      }
      onUnauthenticated?.();
    }

    throw toApiError(error);
  });
}

/** Marks a request as public so no token is attached and no refresh is attempted. */
export const publicRequest = { headers: { 'X-Skip-Auth': '1' } };

/** `rounds.0.durationMinutes` reads as `Round 1 duration minutes`. */
function humanisePath(path: string): string {
  const words: string[] = [];

  for (const segment of path.split('.')) {
    // An index belongs to the thing before it: `rounds.0` is the first round, and the
    // person counts from one even though the array does not.
    if (/^\d+$/.test(segment)) {
      const owner = words.pop();
      if (owner) words.push(`${owner.replace(/s$/, '')} ${Number(segment) + 1}`);
      continue;
    }
    words.push(segment.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase());
  }

  const joined = words.join(' ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

interface FieldIssue {
  path: string;
  message: string;
}

function isFieldIssues(value: unknown): value is FieldIssue[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((i) => i && typeof i === 'object' && typeof (i as FieldIssue).message === 'string')
  );
}

/**
 * The sentence to show someone when a request failed.
 *
 * A rejected form used to surface as "Request validation failed", which names nothing and
 * leaves the person to guess which of a dozen fields the server disliked — on the
 * interview builder there are five separate ways to be rejected, and all of them looked
 * identical. The server has always said exactly which field and why in `details`; this
 * had simply been dropping it.
 *
 * Capped at three issues. Past that the list is longer than the form is readable, and the
 * first few are enough to get someone moving.
 */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof ApiError) {
    if (isFieldIssues(error.details)) {
      const shown = error.details
        .slice(0, 3)
        .map((issue) => (issue.path ? `${humanisePath(issue.path)}: ${issue.message}` : issue.message));
      const more = error.details.length - shown.length;
      return shown.join('. ') + (more > 0 ? `. And ${more} more.` : '');
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}
